// Per-type field layout for the CRM form. Keys prefixed "td." live in the
// backend `type_details` dict; every other key is an existing top-level field.
// Fields not listed for a type are hidden in the UI only — their data is kept.

export const getValue = (form, key) =>
  key.startsWith("td.") ? (form.type_details || {})[key.slice(3)] : form[key];

export const putValue = (form, key, value) =>
  key.startsWith("td.")
    ? { ...form, type_details: { ...(form.type_details || {}), [key.slice(3)]: value } }
    : { ...form, [key]: value };

const t = (key, label, testid, o = {}) => ({ key, label, testid, kind: "text", ...o });
const sel = (key, label, testid, options, o = {}) => t(key, label, testid, { kind: "select", options, ...o });
const chips = (key, label, testid, options) => t(key, label, testid, { kind: "chips", options, wide: true });

const email = t("email", "Email", "crm-f-email");
const phone = t("phone", "Phone", "crm-f-phone");
const twitter = t("twitter", "Twitter / X", "crm-f-twitter");
const instagram = t("instagram", "Instagram", "crm-f-ig");
const bluesky = t("bluesky", "Bluesky", "crm-f-bluesky");
const telegram = t("telegram", "Telegram", "crm-f-telegram");
const faphouse = t("faphouse", "Faphouse", "crm-f-faphouse");
const onlyfans = t("onlyfans", "OnlyFans (VIP)", "crm-f-of");
const onlyfansFree = t("onlyfans_free", "OnlyFans (Free)", "crm-f-of-free");
const justforfans = t("justforfans", "JustForFans", "crm-f-jff");
const linkBio = t("link_in_bio", "Link-in-Bio", "crm-f-linkbio", { placeholder: "https://linktr.ee/..." });
const tiktok = t("tiktok", "TikTok", "crm-f-tiktok");
const country = t("country", "Country", "crm-f-country");
const city = t("city", "City", "crm-f-city");
const languages = t("languages", "Languages", "crm-f-langs", { wide: true, placeholder: "English, Greek, Italian..." });
const position = (label = "Position / Role", o = {}) => t("position", label, "crm-f-pos", o);
const website = t("td.website", "Website", "crm-f-website", { placeholder: "https://..." });
const contact = t("td.primary_contact", "Primary Contact Name", "crm-f-contact");
const availability = (label) => t("td.availability", label, "crm-f-availability", { wide: true });
const contract = (label) => t("td.contract_template_url", label, "crm-f-contract", { placeholder: "Drive/Cloudinary link" });

const PEOPLE_HINT = "first photo is the avatar";
const COMPANY_HINT = "first image is the logo/cover";

const PERFORMER = {
  nameLabel: "Stage Name",
  legalLabel: "Legal Name",
  photoHint: PEOPLE_HINT,
  consent: true,
  lists: [{ key: "shoots", label: "Shoot" }, { key: "shows", label: "Event" }],
  fields: [
    position("Position", { placeholder: "Top / Bottom / Vers" }),
    email, phone,
    twitter, instagram, bluesky, telegram, faphouse, onlyfans, onlyfansFree, justforfans, linkBio,
    country, city,
    t("date_of_birth", "Date of Birth", "crm-f-dob", { kind: "date" }),
    t("body_type", "Body Type", "crm-f-body", { placeholder: "Athletic, Muscular, Slim..." }),
    t("height", "Height (cm)", "crm-f-height", { kind: "number", placeholder: "180" }),
    t("weight", "Weight (kg)", "crm-f-weight", { kind: "number", placeholder: "75" }),
    t("dick_size", "Dick Size", "crm-f-dick", { placeholder: "e.g. 17" }),
    languages,
    t("kinks", "Kinks / Interests", "crm-f-kinks", { wide: true, placeholder: "Leather, Feet, Fisting..." }),
    t("boundaries", "Boundaries / Limits", "crm-f-bounds", { wide: true }),
  ],
};

const STUDIO = {
  nameLabel: "Studio / Agency Name",
  legalLabel: "Legal / Registered Company Name",
  photoHint: COMPANY_HINT,
  studioBlock: true,
  followUpsTitle: "PARTNERSHIP COMMUNICATIONS",
  lists: [{ key: "shoots", label: "Shoot" }],
  fields: [
    t("studio_contact_person", "Primary Contact Name", "crm-f-studio-contact", { placeholder: "John Smith (Talent Director)" }),
    position(),
    email, phone,
    t("studio_website", "Website", "crm-f-studio-website", { placeholder: "https://..." }),
    instagram, twitter, telegram, linkBio,
    country, city,
    languages,
    t("studio_billing_email", "Billing Email", "crm-f-studio-billing", { placeholder: "finance@studio.com" }),
    t("studio_vat_number", "VAT / Tax Number", "crm-f-studio-vat"),
    t("studio_default_payment_terms", "Default Payment Terms", "crm-f-studio-terms", { placeholder: "NET 30, On delivery..." }),
    sel("studio_default_fee_currency", "Default Fee Currency", "crm-f-studio-currency", ["EUR", "USD", "GBP"]),
    t("studio_headquarters_address", "Headquarters Address", "crm-f-studio-hq", { wide: true, kind: "textarea" }),
    chips("td.services", "Services / Specialties", "crm-f-services",
      ["filming", "editing", "photography", "location rental", "equipment rental", "casting", "post-production", "legal support", "other"]),
    sel("td.studio_type", "Studio Type", "crm-f-studio-type",
      ["professional studio", "home studio", "outdoor/location partner", "agency", "other"]),
    t("td.capacity_performers", "Max Performers per Shoot", "crm-f-cap-performers"),
    t("td.capacity_rooms", "Available Rooms / Sets", "crm-f-cap-rooms"),
    t("td.capacity_equipment", "Equipment Available", "crm-f-cap-equipment", { wide: true }),
    availability("Booking Availability / Lead Time"),
    t("studio_contract_template_url", "Contract Template", "crm-f-studio-contract", { placeholder: "Drive/Cloudinary link" }),
    t("studio_model_release_url", "Model Release Template", "crm-f-studio-release", { placeholder: "Drive/Cloudinary link" }),
  ],
};

const BRAND = {
  nameLabel: "Brand / Sponsor Name",
  legalLabel: "Legal / Company Name",
  photoHint: COMPANY_HINT,
  lists: [{ key: "td.history", label: "Campaign" }],
  fields: [
    contact, position(), email, phone, website,
    instagram, twitter, tiktok, linkBio,
    country, city,
    sel("td.industry", "Industry / Category", "crm-f-industry",
      ["adult toys", "lingerie", "supplements", "payment platform", "VPN", "crypto", "fashion", "other"]),
    sel("td.partnership_type", "Partnership Type", "crm-f-partnership",
      ["sponsorship", "affiliate", "product gifting", "paid promotion", "ambassador", "other"]),
    t("td.commission", "Commission / Revenue-Share Details", "crm-f-commission", { wide: true }),
    contract("Contract / Agreement Template"),
    t("td.campaign_notes", "Campaign Notes", "crm-f-campaign-notes", { wide: true, kind: "textarea" }),
  ],
};

const TEAM_ROLES = ["assistant", "moderator", "editor", "admin", "marketing", "support", "driver", "security", "other"];
const TEAM = {
  nameLabel: "Full Name",
  legalLabel: null,
  photoHint: PEOPLE_HINT,
  lists: [{ key: "td.history", label: "Task" }],
  fields: [
    t("td.preferred_name", "Preferred Name / Stage Name", "crm-f-preferred"),
    sel("position", "Role / Position", "crm-f-pos", TEAM_ROLES, { required: true }),
    email, phone,
    instagram, twitter, telegram, linkBio,
    country, city,
    languages,
    t("td.skills", "Skills / Responsibilities", "crm-f-skills", { wide: true }),
    availability("Availability / Working Hours"),
    t("td.payment_details", "Payment Method / Rate Details", "crm-f-payment", { wide: true }),
    t("td.start_date", "Start Date", "crm-f-start-date", { kind: "date" }),
  ],
};

const PRODUCER = {
  nameLabel: "Producer / Company Name",
  legalLabel: "Legal Name",
  photoHint: COMPANY_HINT,
  lists: [{ key: "shoots", label: "Shoot" }],
  fields: [
    contact, position(), email, phone, website,
    instagram, twitter, telegram, linkBio,
    country, city,
    languages,
    sel("td.production_type", "Production Type", "crm-f-production-type",
      ["video", "photography", "livestream", "post-production", "full production"]),
    t("td.services_text", "Services / Specialties", "crm-f-prod-services", { wide: true }),
    t("td.equipment", "Equipment / Crew Available", "crm-f-equipment", { wide: true }),
    t("td.rates", "Rates / Day Rate / Project Rate", "crm-f-rates", { wide: true }),
    availability("Availability"),
    contract("Contract Template"),
  ],
};

const CAMERAMAN = {
  nameLabel: "Full Name",
  legalLabel: null,
  photoHint: PEOPLE_HINT,
  lists: [{ key: "shoots", label: "Shoot" }],
  fields: [
    t("td.preferred_name", "Preferred Name", "crm-f-preferred"),
    email, phone,
    instagram, twitter, telegram, linkBio,
    country, city,
    t("date_of_birth", "Date of Birth (only if needed for age verification)", "crm-f-dob", { kind: "date" }),
    languages,
    t("td.equipment", "Camera / Equipment Owned", "crm-f-equipment", { wide: true }),
    chips("td.specialties", "Specialties", "crm-f-specialties", ["video", "photo", "gimbal", "drone", "lighting", "editing"]),
    t("td.rates", "Day Rate / Hourly Rate", "crm-f-rates"),
    availability("Availability"),
    t("td.portfolio_links", "Portfolio Links", "crm-f-portfolio", { wide: true }),
  ],
};

const EVENT_ORGANIZER = {
  nameLabel: "Organizer / Event Company Name",
  legalLabel: "Legal / Company Name",
  photoHint: COMPANY_HINT,
  lists: [{ key: "td.history", label: "Event" }],
  fields: [
    contact, position(), email, phone, website,
    instagram, twitter, telegram, linkBio,
    country, city,
    languages,
    sel("td.event_type", "Event Type", "crm-f-event-type",
      ["party", "club night", "festival", "private event", "adult event", "other"]),
    t("td.venues", "Typical Venues / Locations", "crm-f-venues", { wide: true }),
    t("td.audience_size", "Audience Size / Capacity", "crm-f-audience"),
    chips("td.event_services", "Services", "crm-f-event-services",
      ["venue booking", "security", "ticketing", "promotion", "artist booking", "production"]),
    availability("Availability / Upcoming Events"),
    contract("Contract / Agreement Template"),
  ],
};

const CONFIG = {
  Escort: PERFORMER, Fan: PERFORMER, Model: PERFORMER,
  Studio: STUDIO, Brand: BRAND, Team: TEAM,
  Producer: PRODUCER, Cameraman: CAMERAMAN, "Event Organizer": EVENT_ORGANIZER,
};

export const configFor = (type) => CONFIG[type] || PERFORMER;

// Returns an error message, or "" when the record can be saved.
export function validateRecord(form) {
  const c = configFor(form.type);
  if (!(form.stage_name || "").trim()) return `${c.nameLabel} required`;
  const missing = c.fields.find((f) => f.required && !String(getValue(form, f.key) ?? "").trim());
  return missing ? `${missing.label} required` : "";
}
